import React, { useMemo, useEffect, useRef, useState } from "react";
import { useGetTakebacksInfinite } from "../../../../api/tes/useGetTakebacksInfinite";
import { ReactComponent as WarningSvg } from "../svgs/warning.svg";
import { ReactComponent as ErrorSvg } from "../svgs/error.svg";
import { ReactComponent as EscalatedSvg } from "../svgs/escalated.svg";
import { useSteps } from "../useSteps";

// StatusBadge component embedded in this file
const STATUS_CONFIG = {
  // Warehouse statuses
  initiated: {
    label: "Initiated",
    backgroundColor: "rgb(55, 65, 81)",
    color: "rgb(209, 213, 219)",
  },
  "checked-in": {
    label: "Checked In",
    backgroundColor: "rgb(113, 63, 18)",
    color: "rgb(251, 191, 36)",
  },
  "warehouse-started": {
    label: "Processing",
    backgroundColor: "rgb(30, 58, 138)",
    color: "rgb(96, 165, 250)",
  },
  "warehouse-ended": {
    label: "Warehouse Ended (Not Paid)",
    backgroundColor: "rgb(113, 63, 18)",
    color: "rgb(251, 191, 36)",
  },
  "payment-create-SUCCEEDED": {
    label: "Payment Issued",
    backgroundColor: "rgb(20, 83, 45)",
    color: "rgb(74, 222, 128)",
  },
  "payment-create-FAILED": {
    label: "Payment Failed",
    backgroundColor: "rgb(127, 29, 29)",
    color: "rgb(252, 165, 165)",
  },
  "in-store-checkin": {
    label: "In-Store Check-In",
    backgroundColor: "rgb(55, 65, 81)",
    color: "rgb(209, 213, 219)",
  },
  "associate-verified": {
    label: "Associate Verified",
    backgroundColor: "rgb(55, 65, 81)",
    color: "rgb(209, 213, 219)",
  },
  "seller-dropoff-accepted": {
    label: "Dropoff Accepted",
    backgroundColor: "rgb(55, 65, 81)",
    color: "rgb(209, 213, 219)",
  },
  // Rejection statuses
  escalated: {
    label: "Escalated",
    backgroundColor: "rgb(153, 27, 27)",
    color: "rgb(254, 202, 202)",
    icon: EscalatedSvg,
  },
  rejected: {
    label: "Rejected",
    backgroundColor: "rgb(220, 38, 38)",
    color: "rgb(254, 226, 226)",
  },
  // Rejection categories
  hazardous: {
    label: "Hazardous",
    backgroundColor: "rgb(113, 63, 18)",
    color: "rgb(251, 191, 36)",
    icon: WarningSvg,
  },
  fraudulent: {
    label: "Fraudulent",
    backgroundColor: "rgb(127, 29, 29)",
    color: "rgb(252, 165, 165)",
    icon: ErrorSvg,
  },
  other: {
    label: "Other",
    backgroundColor: "rgb(30, 58, 138)",
    color: "rgb(96, 165, 250)",
  },
};

const StatusBadge = ({ status, type = "status" }) => {
  const config = STATUS_CONFIG[status];

  if (!config) {
    // Default badge for unknown statuses
    return (
      <span
        className="text-sm font-medium px-4 py-1.5 rounded-full inline-flex items-center"
        style={{
          backgroundColor: "rgb(30, 58, 138)",
          color: "rgb(96, 165, 250)",
        }}
      >
        {type === "status" ? `Other (${status})` : "Unknown"}
      </span>
    );
  }

  const Icon = config.icon;

  return (
    <span
      className="text-sm font-medium px-4 py-1.5 rounded-full inline-flex items-center gap-2"
      style={{
        backgroundColor: config.backgroundColor,
        color: config.color,
      }}
    >
      {Icon && <Icon className="w-4 h-4 fill-current" />}
      {config.label}
    </span>
  );
};

// Main TakebacksTable component
export const TakebacksTable = ({
  filter,
  emptyStateTitle = "No items",
  emptyStateMessage = "Items will appear here",
  showStatusColumn = true,
  tableType = "inventory", // "inventory" or "alerts"
  onRowClick,
  refreshKey, // Add refreshKey prop
}) => {
  const { dropStoreMode } = useSteps();
  const scrollContainerRef = useRef(null);
  const loadMoreRef = useRef(null);
  const itemsPerPage = 100;
  const [copiedId, setCopiedId] = useState(null);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    error,
    fetchNextPage,
    hasNextPage,
  } = useGetTakebacksInfinite({
    limit: itemsPerPage,
    filter,
    enabled: true,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refreshKey, // Pass refreshKey to the hook
  });

  const parseDataField = (dataField) => {
    if (!dataField) return null;

    // If it's already an object, return it
    if (typeof dataField === "object") return dataField;

    // If it's a string, try to parse it
    if (typeof dataField === "string") {
      try {
        return JSON.parse(dataField);
      } catch (e) {
        console.error("Failed to parse data field:", e);
        return null;
      }
    }

    return null;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    // Convert Unix timestamp to milliseconds
    const date = new Date(timestamp * 1000);

    // Format: "10 Oct 23 at 10:14"
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear().toString().slice(-2);
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");

    return `${day} ${month} ${year} at ${hours}:${minutes}`;
  };

  const getLatestStatus = (processingHistory) => {
    if (!processingHistory?.items || processingHistory.items.length === 0) {
      return "pending";
    }

    // Get the last item in the array (most recent status)
    return (
      processingHistory.items[processingHistory.items.length - 1]?.status ||
      "pending"
    );
  };

  const tableData = useMemo(() => {
    if (!data?.pages) return [];

    const result = data.pages
      .flatMap((page) => page?.data?.takebacks?.items || [])
      .filter((takeback) => {
        const parsedData = parseDataField(takeback.data);
        const latestStatus = getLatestStatus(
          takeback.takeback_processing_historys
        );

        // Filter based on drop store mode
        const returnMethod = parsedData?.returnMethod;

        if (dropStoreMode) {
          // Drop store mode ON: show everything except mail
          if (returnMethod === "mail") {
            return false;
          }
        } else {
          // Drop store mode OFF: show only mail
          if (returnMethod !== "mail") {
            return false;
          }
        }

        if (tableType === "inventory") {
          // For inventory table, exclude items with rejectCategory or rejected/escalated status
          // Also exclude in-store-checkin, associate-verified, initiated, and seller-dropoff-accepted statuses
          return (
            !parsedData?.rejectCategory &&
            latestStatus !== "rejected" &&
            latestStatus !== "escalated" &&
            latestStatus !== "in-store-checkin" &&
            latestStatus !== "associate-verified" &&
            latestStatus !== "initiated" &&
            latestStatus !== "seller-dropoff-accepted"
          );
        }

        if (tableType === "alerts") {
          // For alerts table, exclude items that have been resolved (have resolutionCategory)
          return !parsedData?.resolutionCategory;
        }

        return true;
      })
      .map((takeback) => {
        const parsedData = parseDataField(takeback.data);
        const latestStatus = getLatestStatus(
          takeback.takeback_processing_historys
        );

        // Get the latest timestamp from the sorted history
        const sortedHistory = takeback.takeback_processing_historys?.items
          ? [...takeback.takeback_processing_historys.items].sort(
              (a, b) =>
                (b.updated_at || b.processed_at) -
                (a.updated_at || a.processed_at)
            )
          : [];

        const latestUpdate =
          sortedHistory[0]?.updated_at || sortedHistory[0]?.processed_at;

        // For alerts table, always show rejection category
        // For inventory table, show rejection category if status is rejected/escalated
        const displayStatus =
          tableType === "alerts"
            ? parsedData?.rejectCategory
            : (latestStatus === "rejected" || latestStatus === "escalated") &&
                parsedData?.rejectCategory
              ? parsedData.rejectCategory
              : latestStatus;

        // Get tracking number from shippingss if available
        const trackingNumber = takeback.shippingss?.items?.[0]?.tracking_number;
        const displayId = trackingNumber || takeback.id;
        const hasTrackingNumber = !!trackingNumber;

        return {
          id: takeback.id,
          displayId: displayId,
          hasTrackingNumber: hasTrackingNumber,
          sellerId:
            `${takeback.customer_id_belongsTo?.name || ""} ${takeback.customer_id_belongsTo?.last_name || ""}`.trim() ||
            "Unknown",
          status: displayStatus,
          updatedDate: formatDate(latestUpdate),
        };
      })
      .filter(Boolean); // Remove null entries

    return result;
  }, [data, tableType, dropStoreMode]);

  // Data is already paginated by the server
  const paginatedData = tableData;

  // Intersection Observer for infinite scroll
  useEffect(() => {
    // Add delay to prevent observer from triggering during initial render
    const timer = setTimeout(() => {
      if (!scrollContainerRef.current || !loadMoreRef.current) return;
      // Stop observing if there's an error - treat it as no more pages
      if (error) return;
      // Don't set up observer if we're already loading data
      if (isLoading) return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (
            entries[0].isIntersecting &&
            hasNextPage &&
            !isFetchingNextPage &&
            !isLoading
          ) {
            fetchNextPage().catch((err) => {
              // Error is handled by react-query, we just need to prevent
              // unhandled promise rejection
              console.error("Error fetching next page:", err);
            });
          }
        },
        {
          root: scrollContainerRef.current,
          rootMargin: "0px",
          threshold: 1.0,
        }
      );

      const target = loadMoreRef.current;
      if (target) {
        observer.observe(target);
      }

      return () => {
        if (target) {
          observer.unobserve(target);
        }
      };
    }, 100); // Small delay to ensure initial render completes

    return () => clearTimeout(timer);
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, error, isLoading]);

  if (isLoading && !data) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
          <p className="mt-4 text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (error && !data) {
    // Only show full error screen if we have no data at all
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-10 h-10 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <p className="text-xl text-gray-400 mb-2">Error loading data</p>
          <p className="text-gray-500">
            {error.message || "Please try again later"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex-1 overflow-hidden flex flex-col pb-24">
        {/* Table Container */}
        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-y-scroll relative max-h-[calc(100vh-15rem)]"
        >
          {/* Table Header */}
          <div className="sticky top-0 bg-[#060E23] border-b border-t border-[#282F44] px-6 py-3 z-10">
            <div className="grid grid-cols-3 gap-6 text-sm font-extrabold uppercase text-[#E6ECFF]">
              <div>seller & package id</div>
              <div>Updated</div>
              {showStatusColumn && <div>status</div>}
            </div>
          </div>

          {/* Table Body */}
          <div className="pb-4 relative">
            {paginatedData.length > 0 ? (
              paginatedData.map((item) => (
                <div
                  key={item.id}
                  className="grid grid-cols-3 gap-6 px-6 py-4 border-b border-[#282F44] hover:bg-[#1A2237] transition-colors cursor-pointer"
                  onClick={(e) => {
                    // Don't trigger row click if clicking on the first cell
                    if (!e.target.closest(".copy-cell")) {
                      onRowClick?.(item.displayId);
                    }
                  }}
                >
                  <div
                    className="cursor-pointer group copy-cell"
                    onClick={(e) => {
                      // Only copy on Shift+Click
                      if (e.shiftKey) {
                        e.preventDefault();
                        e.stopPropagation();
                        navigator.clipboard.writeText(item.id);
                        setCopiedId(item.id);
                        setTimeout(() => setCopiedId(null), 2000);
                      }
                    }}
                  >
                    <div className="font-medium text-sm">{item.sellerId}</div>
                    <div className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors">
                      <span
                        className={!item.hasTrackingNumber ? "opacity-50" : ""}
                      >
                        {item.displayId}
                      </span>
                      {copiedId === item.id && (
                        <span className="ml-2 text-green-400 text-xs">
                          Copied!
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-sm">{item.updatedDate}</div>
                  {showStatusColumn && (
                    <div>
                      <StatusBadge
                        status={item.status}
                        type={tableType === "alerts" ? "category" : "status"}
                      />
                    </div>
                  )}
                </div>
              ))
            ) : !isLoading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="w-40 h-40 bg-[#1A2237] rounded-full mb-6"></div>
                <p className="text-xl text-gray-400 mb-2">{emptyStateTitle}</p>
                <p className="text-gray-500">{emptyStateMessage}</p>
              </div>
            ) : null}

            {/* Infinite scroll trigger */}
            {paginatedData.length > 0 && (
              <div
                ref={loadMoreRef}
                className="h-20 flex items-center justify-center"
              >
                {isFetchingNextPage && (
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                )}
                {(!hasNextPage || error) && paginatedData.length > 0 && (
                  <p className="text-gray-500 text-sm">
                    Finished loading all items
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
